import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import { SxProps, Theme } from '@mui/material';

import { IScrollThresholds } from '../types/shell';
import { AddressChoiceEnum } from '../types/ui';

export const DEFAULT_LOGO_URL = '/logo.svg';

export const FOOTER_DISCLAIMER =
  'Your address will not be shared or sold, and is only used once for delivering this package.';

export const SUCCESS_TITLE = 'Your form was submitted!';
export const SUCCESS_MESSAGE = 'Your package is being prepared and is on the way to you!';

export const FIXED_ITEMS_INFO = 'Fixed products associated with the campaign — included automatically in every package';
export const getIncludedItemsTitle = (count: number) => `Included items · ${count}`;

export const CUSTOMIZABLE_ITEM_DEFAULT_LABEL_TEXT = 'Input your text to customize the label';
export const CUSTOMIZABLE_ITEM_TEXT_MAX_LENGTH = 25;

export const HERO_IMAGE_WIDTH = 512;
export const HERO_IMAGE_HEIGHT = 288;
export const HERO_IMAGE_WIDTH_EMBED = 360;
export const HERO_IMAGE_HEIGHT_EMBED = 203;
export const HERO_IMAGE_MAX_HEIGHT_MOBILE = 380;

export const HERO_MIN_HEIGHT_DESKTOP = 320;
export const HERO_MIN_HEIGHT_MOBILE = 200;
export const LONG_HERO_VIEWPORT_RATIO = 0.4;
export const HERO_DEFAULT_IMAGE_ASPECT = 2.67;
export const HERO_IMAGE_BACKING_COLOR = '#EFECEA';

export const LOGO_SCALE_MIN = 0.5;
export const LOGO_SCALE_MAX = 2;
export const LOGO_MAX_WIDTH_RATIO = 0.7;
export const LOGO_BASE_HEIGHT_DESKTOP = 32;
export const LOGO_BASE_HEIGHT_MOBILE = 24;
export const LEGACY_LOGO_HEIGHT = 48;
export const LOGO_VERTICAL_PADDING = 1;

export const HEADER_BASE_HEIGHT_DESKTOP = 64;
export const HEADER_BASE_HEIGHT_MOBILE = 56;
/*
 * The bar grows only when the logo would otherwise touch its edges, which on desktop happens
 * at 200% (64px logo) and yields a 66px bar. The mobile logo tops out at 48px, so the mobile
 * bar always stays at its 56px base and needs no ceiling of its own.
 */
export const HEADER_MAX_HEIGHT_DESKTOP = 66;

export const ADDRESS_CHOICE_TABS = [
  {
    label: 'Select from address book',
    value: AddressChoiceEnum.Select,
    Icon: MenuBookOutlinedIcon,
  },
  {
    label: 'Enter custom address',
    value: AddressChoiceEnum.Form,
    Icon: EditOutlinedIcon,
  },
];

export const SEARCH_VISIBLE_THRESHOLD = 2;
export const POPOVER_MAX_HEIGHT = 468;
export const POPOVER_VIEWPORT_PADDING = 8;

export const POPOVER_FOOTER_CLEARANCE = 124;
export const POPOVER_MIN_HEIGHT = 224;
export const ADDRESS_HALF_WIDTH_BREAKPOINT = 1300;
export const RAIL_COLLAPSE_BREAKPOINT = 1118;

// Nav banner
export const SLOT_HEIGHT = 36;
export const HIDE_GRACE_MS = 400;
export const IDLE_COLLAPSE_MS = 300;
export const IDLE_POLL_MS = 320;

// Beat between the sidebar closing on its selected card and the route change, so the card is
// seen taking the selection rather than the page changing out from under the click.
export const SELECTION_CONFIRM_MS = 1000;

// Item card
export const MAX_VISIBLE_SKUS = 4;
export const MAX_THUMBNAILS_WHEN_OVERFLOWING = 3;
export const CARD_MIN_WIDTH = 282;
export const CARD_MAX_WIDTH = 400;

// Details drawer per-item image block: fixed height on desktop, capped height on mobile.
export const DRAWER_IMAGE_HEIGHT_DESKTOP = 297;
export const DRAWER_IMAGE_MAX_HEIGHT_MOBILE = 320;

// Diagonal-stripe empty-state thumbnail placeholder
export const EMPTY_THUMBNAIL_STRIPE_GRADIENT =
  'linear-gradient(135deg, #F2F4F8 25%, #E2E8F0 25%, #E2E8F0 50%, #F2F4F8 50%, #F2F4F8 75%, #E2E8F0 75%)';

// Carousel
export const CAROUSEL_GAP = 16;
export const CARD_PEEK_OFFSET = 40;

export const CAROUSEL_PROGRESS_TRACK_WIDTH = 96;
export const CAROUSEL_PROGRESS_TRACK_HEIGHT = 4;
export const CAROUSEL_PROGRESS_THUMB_MIN_PERCENT = 24;
export const CAROUSEL_PROGRESS_TRANSITION_MS = 200;
export const getCarouselProgressLabel = (current: number, total: number) => `${current} of ${total}`;

export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

// Scroll guidance
export const SETTLE_MS = 250;

export const AT_BOTTOM_EPSILON_PX = 4;

export const PYG_DOWN_HIDE_AT = 0.25;
export const PYG_UP_THRESHOLDS: IScrollThresholds = { showAt: 0.1, hideAt: 0.25 };

export const SCROLL_LANDING_OFFSET_DESKTOP = 88;
export const SCROLL_LANDING_OFFSET_MOBILE = 92;

// Drawers sit above MUI's own scale (modal is 1300); the lightbox must clear them, since it is
export const DRAWER_Z_INDEX = 2100;
export const LIGHTBOX_Z_INDEX = DRAWER_Z_INDEX + 1;
export const TOOLTIP_Z_INDEX = LIGHTBOX_Z_INDEX + 1;
export const SNACKBAR_Z_INDEX = TOOLTIP_Z_INDEX + 1;
// Design system: snackbars auto-hide after 5s and pause while hovered.
export const SNACKBAR_AUTO_HIDE_MS = 5000;
export const SNACKBAR_FOOTER_CLEARANCE = POPOVER_FOOTER_CLEARANCE - 16;
// MUI's own Snackbar offsets, restated so the footer clearance can override them per breakpoint.
export const SNACKBAR_DEFAULT_BOTTOM = { xs: 8, sm: 24 };
export const SNACKBAR_BOTTOM_TRANSITION = 'bottom 300ms cubic-bezier(0.32, 0.72, 0, 1)';
export const SNACKBAR_ACTION_TINT = 0.5;
export const SNACKBAR_TRANSITION_DURATION = { enter: 300, exit: 250 };
export const SNACKBAR_TRANSITION_EASING = { enter: 'cubic-bezier(0, 0, 0.2, 1)', exit: 'cubic-bezier(0.4, 0, 1, 1)' };

export const GALLERY_THUMBNAIL_SIZE = 56;

export const PILL_BORDER_RADIUS = 9999;

// Hover styles are scoped to pointer devices so a tap does not leave a stuck hover state.
export const HOVER_MEDIA = '@media (hover: hover)';

// The picks sheet holds this share of the viewport at every cart size, so the list has room to
// scroll and the sheet does not resize itself as items are added and removed.
export const BOTTOM_SHEET_FIXED_HEIGHT_PERCENT = 75;

// Thumbnail strips are scrolled by dragging or the mouse wheel, so the platform scrollbar is
// hidden: under a short row it is taller than the gap it sits in.
export const HIDDEN_SCROLLBAR_SX: SxProps<Theme> = {
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
};
